import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Pagination,
    Grid,
    Chip,
    Tooltip,
    InputAdornment,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import Api from '../../Services/Api';

/**
 * BlacklistManager Component
 * 
 * Provides an interface for managing the traffic mixer blacklist
 * - View blacklisted combinations
 * - Add new combinations to the blacklist
 * - Remove combinations from the blacklist
 * - Check if a combination is blacklisted
 */
const BlacklistManager = () => {
    // State for blacklist data
    const [blacklist, setBlacklist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // State for pagination
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [perPage, setPerPage] = useState(20);
    
    // State for search/filter
    const [supplyFeedIdFilter, setSupplyFeedIdFilter] = useState('');
    const [subIdFilter, setSubIdFilter] = useState('');
    
    // State for adding new entries
    const [newSupplyFeedId, setNewSupplyFeedId] = useState('');
    const [newSubId, setNewSubId] = useState('');
    const [addingEntry, setAddingEntry] = useState(false);
    
    // State for checking blacklist status
    const [checkSupplyFeedId, setCheckSupplyFeedId] = useState('');
    const [checkSubId, setCheckSubId] = useState('');
    const [checkResult, setCheckResult] = useState(null);
    const [checking, setChecking] = useState(false);
    
    // State for delete confirmation
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [entryToDelete, setEntryToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // State for notifications
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });
    
    // Fetch blacklist data
    const fetchBlacklist = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const params = {
                page: page,
                per_page: perPage
            };
            
            if (supplyFeedIdFilter) {
                params.supply_feed_id = supplyFeedIdFilter;
            }
            
            if (subIdFilter) {
                params.sub_id = subIdFilter;
            }
            
            const response = await Api.getBlacklist(params);
            
            setBlacklist(response.data);
            setTotalPages(response.pagination.last_page);
            
            setLoading(false);
        } catch (err) {
            console.error('Error fetching blacklist:', err);
            setError('Failed to load blacklist data. Please try again.');
            setLoading(false);
        }
    };
    
    // Add a new entry to the blacklist
    const addToBlacklist = async () => {
        try {
            setAddingEntry(true);
            
            const response = await Api.addToBlacklist({
                supply_feed_id: newSupplyFeedId,
                sub_id: newSubId
            });
            
            setNotification({
                open: true,
                message: 'Added to blacklist successfully',
                severity: 'success'
            });
            
            // Reset form fields
            setNewSupplyFeedId('');
            setNewSubId('');
            
            // Refresh the blacklist
            fetchBlacklist();
            
            setAddingEntry(false);
        } catch (err) {
            console.error('Error adding to blacklist:', err);
            
            let errorMessage = 'Failed to add to blacklist. Please try again.';
            
            // Check for specific error responses
            if (err.response && err.response.status === 409) {
                errorMessage = 'This combination is already blacklisted.';
            } else if (err.response && err.response.data && err.response.data.messages) {
                errorMessage = Object.values(err.response.data.messages).flat().join(' ');
            }
            
            setNotification({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
            
            setAddingEntry(false);
        }
    };
    
    // Remove an entry from the blacklist
    const removeFromBlacklist = async () => {
        if (!entryToDelete) return;
        
        try {
            setDeleting(true);
            
            await Api.removeFromBlacklist({
                id: entryToDelete.id
            });
            
            setNotification({
                open: true,
                message: 'Removed from blacklist successfully',
                severity: 'success'
            });
            
            // Close the dialog
            setDeleteDialogOpen(false);
            setEntryToDelete(null);
            
            // Refresh the blacklist
            fetchBlacklist();
            
            setDeleting(false);
        } catch (err) {
            console.error('Error removing from blacklist:', err);
            
            setNotification({
                open: true,
                message: 'Failed to remove from blacklist. Please try again.',
                severity: 'error'
            });
            
            setDeleting(false);
        }
    };
    
    // Check if a combination is blacklisted
    const checkBlacklistStatus = async () => {
        try {
            setChecking(true);
            setCheckResult(null);
            
            const response = await Api.checkBlacklistStatus({
                supply_feed_id: checkSupplyFeedId,
                sub_id: checkSubId
            });
            
            setCheckResult(response);
            setChecking(false);
        } catch (err) {
            console.error('Error checking blacklist status:', err);
            
            setNotification({
                open: true,
                message: 'Failed to check blacklist status. Please try again.',
                severity: 'error'
            });
            
            setChecking(false);
        }
    };
    
    // Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
    };
    
    // Handle search/filter
    const handleSearch = () => {
        setPage(1); // Reset to first page when searching
        fetchBlacklist();
    };
    
    // Handle delete button click
    const handleDeleteClick = (entry) => {
        setEntryToDelete(entry);
        setDeleteDialogOpen(true);
    };
    
    // Close notification
    const handleCloseNotification = () => {
        setNotification({
            ...notification,
            open: false
        });
    };
    
    // Fetch data when component mounts or dependencies change
    useEffect(() => {
        fetchBlacklist();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage]);
    
    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'white' }}>
                Traffic Mixer Blacklist Manager
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.7)' }}>
                Manage blacklisted combinations of Supply Feed IDs and Sub IDs to prevent them from being selected by the Traffic Mixer.
            </Typography>
            
            {/* Add new entry section */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: '#1e2130' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                    Add to Blacklist
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Supply Feed ID"
                            variant="outlined"
                            value={newSupplyFeedId}
                            onChange={(e) => setNewSupplyFeedId(e.target.value)}
                            disabled={addingEntry}
                            type="number"
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Sub ID"
                            variant="outlined"
                            value={newSubId}
                            onChange={(e) => setNewSubId(e.target.value)}
                            disabled={addingEntry}
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={addingEntry ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
                            onClick={addToBlacklist}
                            disabled={!newSupplyFeedId || !newSubId || addingEntry}
                            fullWidth
                            sx={{ height: '56px' }}
                        >
                            {addingEntry ? 'Adding...' : 'Add to Blacklist'}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            
            {/* Check blacklist status section */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: '#1e2130' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                    Check Blacklist Status
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Supply Feed ID"
                            variant="outlined"
                            value={checkSupplyFeedId}
                            onChange={(e) => setCheckSupplyFeedId(e.target.value)}
                            disabled={checking}
                            type="number"
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Sub ID"
                            variant="outlined"
                            value={checkSubId}
                            onChange={(e) => setCheckSubId(e.target.value)}
                            disabled={checking}
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            color="info"
                            startIcon={checking ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                            onClick={checkBlacklistStatus}
                            disabled={!checkSupplyFeedId || !checkSubId || checking}
                            fullWidth
                            sx={{ height: '56px' }}
                        >
                            {checking ? 'Checking...' : 'Check Status'}
                        </Button>
                    </Grid>
                    
                    {checkResult && (
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Alert 
                                severity={checkResult.is_blacklisted ? "warning" : "success"}
                                icon={checkResult.is_blacklisted ? <BlockIcon /> : null}
                                sx={{ 
                                    bgcolor: checkResult.is_blacklisted ? 'rgba(237, 108, 2, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                                    color: checkResult.is_blacklisted ? '#ed6c02' : '#2e7d32',
                                    '& .MuiAlert-icon': {
                                        color: checkResult.is_blacklisted ? '#ed6c02' : '#2e7d32'
                                    }
                                }}
                            >
                                <Typography variant="body1">
                                    Supply Feed ID <strong>{checkResult.supply_feed_id}</strong> with Sub ID <strong>{checkResult.sub_id}</strong> is 
                                    {checkResult.is_blacklisted ? ' blacklisted' : ' not blacklisted'}.
                                </Typography>
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </Paper>
            
            {/* Search/filter section */}
            <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: '#1e2130' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                    Search Blacklist
                </Typography>
                
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Supply Feed ID"
                            variant="outlined"
                            value={supplyFeedIdFilter}
                            onChange={(e) => setSupplyFeedIdFilter(e.target.value)}
                            type="number"
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            label="Sub ID"
                            variant="outlined"
                            value={subIdFilter}
                            onChange={(e) => setSubIdFilter(e.target.value)}
                            InputLabelProps={{
                                style: { color: 'rgba(255,255,255,0.7)' }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.3)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(255,255,255,0.5)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main'
                                    }
                                }
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Button
                            variant="contained"
                            color="secondary"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            fullWidth
                            sx={{ height: '56px' }}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            
            {/* Blacklist table */}
            <Paper elevation={3} sx={{ bgcolor: '#1e2130' }}>
                <Typography variant="h6" sx={{ p: 2, color: 'white' }}>
                    Blacklisted Combinations
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ m: 2 }}>
                        {error}
                    </Alert>
                )}
                
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : blacklist.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            No blacklisted combinations found.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ maxHeight: 440 }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ bgcolor: '#2c3040', color: 'white' }}>ID</TableCell>
                                        <TableCell sx={{ bgcolor: '#2c3040', color: 'white' }}>Supply Feed ID</TableCell>
                                        <TableCell sx={{ bgcolor: '#2c3040', color: 'white' }}>Sub ID</TableCell>
                                        <TableCell sx={{ bgcolor: '#2c3040', color: 'white' }}>Created At</TableCell>
                                        <TableCell sx={{ bgcolor: '#2c3040', color: 'white' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {blacklist.map((entry) => (
                                        <TableRow key={entry.id} hover>
                                            <TableCell sx={{ color: 'white' }}>{entry.id}</TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                <Chip 
                                                    label={entry.supply_feed_id} 
                                                    color="primary" 
                                                    variant="outlined" 
                                                    size="small" 
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                <Chip 
                                                    label={entry.sub_id} 
                                                    color="secondary" 
                                                    variant="outlined" 
                                                    size="small" 
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: 'white' }}>
                                                {new Date(entry.created_at).toLocaleString()}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title="Remove from blacklist">
                                                    <IconButton 
                                                        color="error" 
                                                        onClick={() => handleDeleteClick(entry)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <Pagination 
                                count={totalPages} 
                                page={page} 
                                onChange={handlePageChange} 
                                color="primary" 
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        color: 'white'
                                    }
                                }}
                            />
                        </Box>
                    </>
                )}
            </Paper>
            
            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                PaperProps={{
                    sx: {
                        bgcolor: '#1e2130',
                        color: 'white'
                    }
                }}
            >
                <DialogTitle id="alert-dialog-title">
                    Confirm Removal
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Are you sure you want to remove Supply Feed ID <strong>{entryToDelete?.supply_feed_id}</strong> with Sub ID <strong>{entryToDelete?.sub_id}</strong> from the blacklist?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialogOpen(false)} 
                        color="primary"
                        disabled={deleting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={removeFromBlacklist} 
                        color="error" 
                        autoFocus
                        disabled={deleting}
                        startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                    >
                        {deleting ? 'Removing...' : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
            
            {/* Notification snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={handleCloseNotification} 
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BlacklistManager; 